import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import { ModalMode } from '../../../stores/Policy/PolicyModalStore';
import PolicyStore from '../../../stores/Policy/PolicyStore';
import RedirectMatchCriteriaStore, {
    SENDER_TABS
} from '../../../stores/Policy/RedirectRules/RedirectMatchCriteriaStore';
import StatefulBulkEditor, { BULK_EDITOR_STATE } from '../../BulkEditor/StatefulBulkEditor';
import CheckboxContainer from '../../CheckboxContainer/CheckboxContainer';
import FuseConsoleCardFooter from '../../ConsoleFragments/FuseConsoleCardFooter';
import { Card, FTBody, FTLabel } from '../../Fuse';
import FuseTabs from '../../Fuse/FuseTabs';
import FuseProgressIndicator from '../../FuseControls/FuseProgressIndicator';
import FuseReadOnlyCheckbox from '../../FuseControls/FuseReadOnlyCheckbox';
import FuseReadOnlySelect from '../../FuseControls/FuseReadOnlySelect';
import { And } from '../ConnectionBlock/ConnectionBlock';
import CategoriesSection from '../CategoriesSection/CategoriesSection';
import PolicySection from '../PolicySection';
import PolicySectionHeader from '../PolicySectionHeader/PolicySectionHeader';
import { ConsoleBody } from '../../Text/Text';
import FuseAlert from '../../FuseControls/FuseAlert';
import './RedirectMatchCriteria.css';

const block = b.lock('MatchCriteria');

class RedirectMatchCriteria extends Component {
    static propTypes = {
        store: RedirectMatchCriteriaStore.PropType.isRequired,
        readOnly: PropTypes.bool,
        policy: PolicyStore.PropType.isRequired,
    };

    componentDidMount() {
        this.props.store.load();
        this.props.store.loadLastSyncGroup();
    }

    componentWillUnmount() {
        const { policy } = this.props;
        policy.redirectRules.showReminderAlert.setValue(false);
        policy.redirectRules.showNoBrowsingRoleEnabledAlert.setValue(false);
    }

    bulkOptions = (emails) => {
        const { policy, store } = this.props;
        const { policyModal } = policy.redirectRules.policyTable;

        switch (policyModal.mode) {
        case ModalMode.DELETE:
        case ModalMode.VIEW:
            return { type: BULK_EDITOR_STATE.READ_ONLY };
        case ModalMode.EDIT_CONFIRM:
            return {
                type: BULK_EDITOR_STATE.EDIT_CONFIRM,
                oldItems: emails ? store.originalValue.senderEmailAddresses : store.originalValue.senderEmailDomains,
                newItems: emails ? store.senderEmailAddresses.getValue() : store.senderEmailDomains.getValue(),
                removedItemsSearch: emails ? store.editConfirmSenderEmailDomainsAddedSearch :
                    store.editConfirmSenderEmailAddressesAddedSearch,
                addedItemsSearch: emails ? store.editConfirmSenderEmailAddressesRemovedSearch :
                    store.editConfirmSenderEmailDomainsRemovedSearch
            };
        default:
            return { type: BULK_EDITOR_STATE.NORMAL, required: true };
        }
    }

    renderDomains = ({ readOnly, store }) => <StatefulBulkEditor
        mode={this.bulkOptions()}
        store={store.senderEmailDomains}
        title={''}
        showDomainsGuidelines
        validUrlText={'Entries'}
        placeholder={'Enter or paste Email Domains'}
        searchPlaceholder={'Search Email Domains'}
        subTitle={'Enter Sender Email Domains below (1 entry per line).'}
        errorMassage={'Invalid Email Domain'}
        wrapperClassName={block('BulkEditorWrapper')}
        readOnlyCardClassName={block('BulkCard', { readOnly, type: 'domains' })}
        entityName={(readOnly ? 'Email Sender - ' : '') + 'Email Domains'}
        readOnlyHeader={<FTBody className={block('BulkEditorReadOnlyHeader')}> Email Sender - Email Domains </FTBody>}
    />;

    renderEmails = ({ readOnly, store }) => <StatefulBulkEditor
        mode={this.bulkOptions(true)}
        store={store.senderEmailAddresses}
        title={''}
        action={null}
        validUrlText={'Entries'}
        placeholder={'Enter or paste Email Addresses'}
        searchPlaceholder={'Search Email Addresses'}
        subTitle={'Enter Sender Email Addresses below (1 entry per line).'}
        errorMassage={'Invalid Email Address'}
        wrapperClassName={block('BulkEditorWrapper')}
        readOnlyCardClassName={block('BulkCard', { readOnly })}
        entityName={(readOnly ? 'Email Sender - ' : '') + 'Email Addresses'}
        readOnlyHeader={<FTBody className={block('BulkEditorReadOnlyHeader')}> Email Sender - Email Addresses </FTBody>}
    />;

    renderSender = () => {
        const { store, policy } = this.props;
        const { readOnly } = this.props;
        const policyProps = { grayBackground: !readOnly, readOnly };
        const disable = policy.redirectRules.showNoBrowsingRoleEnabledAlert.getValue();
        return <PolicySection {...policyProps} className={block('PaddingOffset')}>
            <PolicySectionHeader
                title={'Email Sender'}
                tooltipText={!readOnly ? `The sender of an email that contains a TAP rewritten URL.
                The sender’s email address is encoded in the URL and can match to a
                Redirect Rule’s Email Sender using either Domain or Full Email Address.` : null}
            />

            <CheckboxContainer className={block('BrowsingSubHeader')}>
                {(!readOnly || store.matchAllSenderEmail.isTrue) ? <FuseReadOnlyCheckbox
                    readOnly={readOnly}
                    disabled={disable}
                    store={store.matchAllSenderEmail}
                    label={!readOnly ? 'Match All Email Senders' :
                        <FTBody>All Email Senders</FTBody>}
                /> : null}
            </CheckboxContainer>

            {!readOnly && !store.matchAllSenderEmail.isTrue ? <div className={block('SenderTabsContainer')}>
                <FuseTabs
                    containerClassName={block('SenderTabsHeader')}
                    store={store.activeTab}
                    tabs={[
                        { label: 'Email Domains', value: SENDER_TABS.DOMAINS },
                        { label: 'Email Addresses', value: SENDER_TABS.EMAILS },
                    ]}
                />
                {store.activeTab.isChecked(SENDER_TABS.DOMAINS) ? this.renderDomains(this.props) : null}
                {store.activeTab.isChecked(SENDER_TABS.EMAILS) ? this.renderEmails(this.props) : null}
            </div> : null}

            {readOnly && !store.matchAllSenderEmail.isTrue ? <Fragment>
                {this.props.store.senderEmailDomains.urlBulkEditorStore.bulkEditorStore.urlPatterns.length ?
                    this.renderDomains(this.props) : null}
                {this.props.store.senderEmailAddresses.urlBulkEditorStore.bulkEditorStore.urlPatterns.length ?
                    this.renderEmails(this.props) : null}
            </Fragment> : null}

            {!readOnly ? <FuseConsoleCardFooter
                className={block('SectionFooter')}
                text={'At least one Email Domain or Address is required. PPS 8.18.2 or higher is also required.'}
            /> : null}
        </PolicySection>;
    }

    render() {
        const { store, policy } = this.props;
        let { readOnly } = this.props;
        const policyProps = {
            grayBackground: !readOnly,
            readOnly
        };
        readOnly = readOnly || store.isDefault;
        const lastSyncGroupText = store.lastSyncGroupDate ? `Last Synchronization: ${store.lastSyncGroupDate}` : '';
        const showReminderAlert = policy.redirectRules.showReminderAlert.getValue();
        const disable = policy.redirectRules.showNoBrowsingRoleEnabledAlert.getValue();

        return <Card title="Match Criteria">
            <PolicySection {...policyProps}>
                <PolicySectionHeader
                    title={<div className={block('PPSCloudSectionTitle')}>
                        PPS Cloud User Sync Groups
                        {readOnly && lastSyncGroupText ? <ConsoleBody>&nbsp;- {lastSyncGroupText}</ConsoleBody> : null}
                    </div>}
                    tooltipText={`The groups synchronized and/or defined within PPS Cloud User Sync.
                    Each TAP rewritten URL includes the recipient user’s email,
                    and can match to a Redirect Rule’s PPS Cloud User Sync Groups.`}
                />
                {showReminderAlert ? (
                    <FuseAlert
                        type="warning"
                        title="REMINDER: In Step 2 of New Policies Migration"
                        className={block('ReminderAlertBlock')}
                    >
                        <FTBody >
                            Please ensure each users redirected by this rule matches at least one
                            <b> enabled Browsing Role </b>or
                            the system may produce unexpected results, such as applying the Default Legacy
                            Policy.
                        </FTBody>
                    </FuseAlert>
                ) : null}
                {store.loadLastSyncStatus.isLoading ? <><FuseProgressIndicator size="small" /></> : <>
                    <CheckboxContainer className={block('BrowsingSubHeader')}>
                        {(!readOnly || store.matchAllUserGroups.isTrue) ? <FuseReadOnlyCheckbox
                            readOnly={readOnly}
                            disabled={disable}
                            store={store.matchAllUserGroups}
                            label={!readOnly ? 'Match All Users' : <FTBody>All Users</FTBody>}
                        /> : null}
                    </CheckboxContainer>
                    {!readOnly && lastSyncGroupText ?
                        <ConsoleBody className={block('LastSyncGroup')}>{lastSyncGroupText}</ConsoleBody> : null}
                    {!store.matchAllUserGroups.isTrue ? <Fragment>
                        <FuseReadOnlySelect
                            containerClassName={!readOnly ? block('TapGroups') : null}
                            readOnly={readOnly}
                            store={store.tapUsersGroups}
                            selectAllEnabled
                            fullWidth
                            searchable
                            searchLabel="Search by Group Name or Email"
                            noOptionsLabel="No entries found"
                            placeholder={readOnly ? 'No Group Chosen' : 'Select Groups'}
                            loading={store.tapUsersGroups.searchStatus.isLoading}
                            onFilter={store.tapUsersGroups.searchHandle}
                            disabled={disable}
                        />
                        {!readOnly && store.referencesUnSyncedGroups() ?
                            <FTLabel className={block('DisabledLegend')}>
                                User Groups displayed in red are not available in the source system for synchronization.
                            </FTLabel> : null
                        }
                    </Fragment> : null}
                    {!readOnly ? <FuseConsoleCardFooter
                        className={block('SectionFooter')}
                        text={'At least one PPS Cloud User Sync Group is required to be selected.'}
                    /> : null}
                </>}
            </PolicySection>

            {!readOnly ? <And /> : null}

            <CategoriesSection
                store={policy.redirectRules.policyTable.policyModal.sections.categories}
                readOnly={readOnly}
            />

            {!readOnly ? <And /> : null}

            {this.renderSender()}
        </Card>;
    }
}

export default inject('policy')(observer(RedirectMatchCriteria));
